import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Package, Plus, DollarSign, TrendingUp, Pencil, Trash2, X, Check } from 'lucide-react'
import { PageHeader, StatCard, EmptyState, Button } from '@/components/ui'
import { useProducts } from '@/hooks/useProducts'

const fmt = (n: number) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n)

const EMPTY_FORM = { name: '', sellingPrice: '', variableCost: '', unit: 'unit', notes: '' }

export function ProductsPage() {
  const { businessId } = useParams<{ businessId: string }>()
  const { products, isLoading, createProduct, updateProduct, deleteProduct } = useProducts(businessId)
  const [showForm, setShowForm] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  const activeProducts = products.filter(p => p.isActive === 1)
  const avgSellingPrice = activeProducts.length ? activeProducts.reduce((s, p) => s + p.sellingPrice, 0) / activeProducts.length : 0
  const avgMargin = activeProducts.length ? activeProducts.reduce((s, p) => s + (p.grossMarginPct ?? 0), 0) / activeProducts.length : 0

  const totalFixedCostEstimate = 0 // will be pulled from costs page

  const openCreate = () => { setEditId(null); setForm(EMPTY_FORM); setShowForm(true) }
  const openEdit = (p: typeof products[0]) => {
    setEditId(p.id)
    setForm({ name: p.name, sellingPrice: String(p.sellingPrice), variableCost: String(p.variableCost), unit: p.unit, notes: p.notes ?? '' })
    setShowForm(true)
  }
  const closeForm = () => { setShowForm(false); setEditId(null); setForm(EMPTY_FORM) }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    const payload = {
      name: form.name,
      sellingPrice: parseInt(form.sellingPrice) || 0,
      variableCost: parseInt(form.variableCost) || 0,
      unit: form.unit,
      isActive: 1,
      notes: form.notes || null
    }
    if (editId) await updateProduct(editId, payload)
    else await createProduct(payload)
    setSaving(false)
    closeForm()
  }

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Hapus produk "${name}"?`)) return
    await deleteProduct(id)
  }

  const handleToggleActive = async (p: typeof products[0]) => {
    await updateProduct(p.id, { isActive: p.isActive === 1 ? 0 : 1 })
  }

  return (
    <div className="page-container">
      <PageHeader
        title="Produk & Harga"
        description="Kelola produk/jasa, harga jual, HPP per unit, dan contribution margin."
        breadcrumbs={[
          { label: 'Bisnis Saya', href: '/businesses' },
          { label: 'Overview', href: `/businesses/${businessId}` },
          { label: 'Produk & Harga' },
        ]}
        action={
          <Button id="products-add" variant="primary" size="sm" leftIcon={<Plus size={14} />} onClick={openCreate}>
            Tambah Produk
          </Button>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Produk Aktif" value={String(activeProducts.length)} icon={Package} subtitle="Terdaftar" />
        <StatCard title="Avg. Harga Jual" value={activeProducts.length ? fmt(avgSellingPrice) : 'Rp 0'} icon={DollarSign} subtitle="Per unit" />
        <StatCard title="Avg. Gross Margin" value={activeProducts.length ? `${avgMargin.toFixed(1)}%` : '—'} icon={TrendingUp} subtitle="% dari harga jual" />
        <StatCard title="Total Produk" value={String(products.length)} icon={Package} subtitle="Aktif + nonaktif" />
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">{editId ? 'Edit Produk' : 'Tambah Produk'}</h2>
              <button type="button" onClick={closeForm} className="text-slate-400 hover:text-white"><X size={20} /></button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-400 block mb-1">Nama Produk / Jasa *</label>
                <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-500"
                  placeholder="Contoh: Kopi Susu" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Harga Jual (IDR) *</label>
                  <input required type="number" min="0" value={form.sellingPrice} onChange={e => setForm(f => ({ ...f, sellingPrice: e.target.value }))}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-500"
                    placeholder="18000" />
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">HPP / Biaya Var. (IDR) *</label>
                  <input required type="number" min="0" value={form.variableCost} onChange={e => setForm(f => ({ ...f, variableCost: e.target.value }))}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-500"
                    placeholder="9000" />
                </div>
              </div>
              {form.sellingPrice && form.variableCost && parseInt(form.sellingPrice) > 0 && (
                <div className="bg-slate-800/50 rounded-lg p-3 text-sm">
                  <div className="flex justify-between text-slate-400">
                    <span>Contribution Margin</span>
                    <span className="text-emerald-400 font-medium">{fmt(parseInt(form.sellingPrice) - parseInt(form.variableCost))}</span>
                  </div>
                  <div className="flex justify-between text-slate-400 mt-1">
                    <span>Gross Margin</span>
                    <span className="text-emerald-400 font-medium">{(((parseInt(form.sellingPrice) - parseInt(form.variableCost)) / parseInt(form.sellingPrice)) * 100).toFixed(1)}%</span>
                  </div>
                </div>
              )}
              <div>
                <label className="text-sm text-slate-400 block mb-1">Unit</label>
                <input value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-500"
                  placeholder="pcs, cup, kg..." />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Catatan (opsional)</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-500 resize-none" />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="ghost" size="sm" onClick={closeForm} className="flex-1">Batal</Button>
              <Button type="submit" variant="primary" size="sm" className="flex-1" disabled={saving}>
                {saving ? 'Menyimpan...' : (editId ? 'Simpan Perubahan' : 'Tambah Produk')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {isLoading ? (
        <div className="text-slate-500 text-center py-8">Memuat data produk...</div>
      ) : products.length === 0 ? (
        <EmptyState
          icon={Package}
          title="Belum ada produk"
          description="Tambahkan produk atau jasa beserta harga jual dan biaya per unit untuk menghitung contribution margin dan BEP."
          action={{ label: 'Tambah Produk Pertama', onClick: openCreate }}
        />
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-800/50 text-slate-400 border-b border-slate-800">
              <tr>
                <th className="px-5 py-3 font-medium">Produk</th>
                <th className="px-5 py-3 font-medium text-right">Harga Jual</th>
                <th className="px-5 py-3 font-medium text-right">HPP</th>
                <th className="px-5 py-3 font-medium text-right">Contribution Margin</th>
                <th className="px-5 py-3 font-medium text-right">Gross Margin</th>
                <th className="px-5 py-3 font-medium text-center">Status</th>
                <th className="px-5 py-3 font-medium text-center">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {products.map(p => {
                const cm = p.contributionMargin ?? (p.sellingPrice - p.variableCost)
                const gm = p.grossMarginPct ?? (p.sellingPrice > 0 ? (cm / p.sellingPrice * 100) : 0)
                return (
                  <tr key={p.id} className={`hover:bg-slate-800/20 transition-colors ${p.isActive === 0 ? 'opacity-50' : ''}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium text-slate-200">{p.name}</div>
                      <div className="text-xs text-slate-500">/{p.unit}</div>
                    </td>
                    <td className="px-5 py-3 text-right text-slate-300">{fmt(p.sellingPrice)}</td>
                    <td className="px-5 py-3 text-right text-slate-400">{fmt(p.variableCost)}</td>
                    <td className="px-5 py-3 text-right text-emerald-400 font-medium">{fmt(cm)}</td>
                    <td className="px-5 py-3 text-right">
                      <span className={`font-medium ${gm >= 40 ? 'text-emerald-400' : gm >= 20 ? 'text-amber-400' : 'text-rose-400'}`}>
                        {gm.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-5 py-3 text-center">
                      <button onClick={() => handleToggleActive(p)}
                        className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase transition-colors ${p.isActive === 1 ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20' : 'bg-slate-700/50 text-slate-500 border border-slate-600 hover:bg-slate-700'}`}>
                        {p.isActive === 1 ? 'Aktif' : 'Nonaktif'}
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => openEdit(p)} className="p-1 text-slate-400 hover:text-white transition-colors rounded"><Pencil size={14} /></button>
                        <button onClick={() => handleDelete(p.id, p.name)} className="p-1 text-slate-400 hover:text-rose-400 transition-colors rounded"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
